/**
 * Feature 099 - Checkr Background Check Integration (Vetting)
 * Mutation hooks for override/decline/restart/start — replaces the
 * mocks/vetting.store.ts write methods. Each invalidates the vetting-status
 * queries (single and batched) so the UI reflects the server's real state
 * on the next read, rather than trusting an optimistic local update for
 * something audit-sensitive.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { overrideCheck, declineCheck, restartCheck, startCheck } from '../api/vetting.api';
import type { VettingCheckSnapshot } from '../types/vetting.types';
import { vettingKeys } from './useProviderVettingStatus';
import { providerKeys } from '@/features/admin-portal/hooks/useAdminProviders';

export function useOverrideVettingCheck(): UseMutationResult<
  VettingCheckSnapshot,
  Error,
  { checkId: string; providerId: string; note: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ checkId, note }) => overrideCheck(checkId, note),
    // Prefix match refreshes the single-provider query AND any batch (Pending tab) query
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vettingKeys.all }),
  });
}

export function useDeclineVettingCheck(): UseMutationResult<
  VettingCheckSnapshot,
  Error,
  { checkId: string; providerId: string; note?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ checkId, note }) => declineCheck(checkId, note),
    // Decline also sets the provider's vettingStatus to REJECTED — refresh provider
    // caches (detail, lists, pending count) too, or the editor would re-submit its
    // stale status on the next save and silently revert the rejection.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: vettingKeys.all }),
        queryClient.invalidateQueries({ queryKey: providerKeys.all }),
      ]),
  });
}

export function useRestartVettingCheck(): UseMutationResult<
  VettingCheckSnapshot,
  Error,
  { checkId: string; providerId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ checkId }) => restartCheck(checkId),
    // Prefix match refreshes the single-provider query AND any batch (Pending tab) query
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vettingKeys.all }),
  });
}

export function useStartVettingCheck(): UseMutationResult<
  { providerId: string; started: boolean },
  Error,
  { providerId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId }) => startCheck(providerId),
    // Prefix match refreshes the single-provider query AND any batch (Pending tab) query
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vettingKeys.all }),
  });
}
